import { VisuallyHidden } from '@monorepo/shared/componentsV2/VisuallyHidden';
import { colors } from '@monorepo/shared/themes/colors';
import CompletedIcon from '@svg/completed.svg';
import FailureIcon from '@svg/m-event-calendar-failure.svg';
import IncompleteEmptyCircleIcon from '@svg/m-event-calendar-incomplete-empty-circle.svg';
import IncompleteIcon from '@svg/m-event-calendar-incomplete.svg';
import { ComplianceCalendarEventStatus } from 'mapistry-shared';
import React from 'react';
import styled from 'styled-components';

const EventStatusIcon = styled.div<{
  color: string;
}>`
  display: flex;
  align-items: center;
  color: ${(props) => props.color};

  & .m-icon {
    width: unset;
    margin: 0 3px 0 2px;
    font-size: 1rem;
  }
`;

const COLORS = {
  COMPLETE: colors.green,
  EXCEPTION: colors.builderBlue,
  FUTURE_INACTIVE: colors.graybbb,
  INCOMPLETE: colors.gray333,
  NOT_NEEDED: colors.darkGreen, // as-needed, extra, ...
  OVERDUE: colors.darkRed,
} as const;

const getDetailsForEventStatusIndicator = (
  status: ComplianceCalendarEventStatus,
) => {
  let Icon;
  let statusText;
  let color;

  switch (status) {
    case ComplianceCalendarEventStatus.EXCEPTION:
      Icon = CompletedIcon;
      statusText = 'exception';
      color = COLORS.EXCEPTION;
      break;
    case ComplianceCalendarEventStatus.COMPLETE:
      Icon = CompletedIcon;
      statusText = 'complete';
      color = COLORS.COMPLETE;
      break;
    case ComplianceCalendarEventStatus.OVERDUE_NOT_STARTED:
      Icon = FailureIcon;
      statusText = 'overdue';
      color = COLORS.OVERDUE;
      break;
    case ComplianceCalendarEventStatus.OVERDUE_BUT_STARTED:
      Icon = IncompleteIcon;
      statusText = 'overdue (started)';
      color = COLORS.OVERDUE;
      break;
    case ComplianceCalendarEventStatus.INCOMPLETE_NOT_STARTED:
      Icon = IncompleteEmptyCircleIcon;
      statusText = 'incomplete';
      color = COLORS.INCOMPLETE;
      break;
    case ComplianceCalendarEventStatus.INCOMPLETE_BUT_STARTED:
      Icon = IncompleteIcon;
      statusText = 'incomplete (started)';
      color = COLORS.INCOMPLETE;
      break;
    case ComplianceCalendarEventStatus.FUTURE_INACTIVE:
      Icon = IncompleteEmptyCircleIcon;
      statusText = 'future (disabled)';
      color = COLORS.FUTURE_INACTIVE;
      break;
    case ComplianceCalendarEventStatus.NOT_NEEDED:
      Icon = IncompleteIcon;
      statusText = 'incomplete';
      color = COLORS.NOT_NEEDED;
      break;

    default: {
      const exhaustiveCheck: never = status;
      throw new Error(
        `Invalid compliance calendar event status: ${exhaustiveCheck}`,
      );
    }
  }

  return { Icon, statusText, color };
};

type EventStatusIndicatorProps = {
  className?: string;
  status: ComplianceCalendarEventStatus;
};

export const EventStatusIndicator = ({
  className,
  status,
}: EventStatusIndicatorProps) => {
  const { Icon, statusText, color } = getDetailsForEventStatusIndicator(status);

  return (
    <EventStatusIcon className={className} color={color}>
      <VisuallyHidden>{statusText}:</VisuallyHidden>
      <Icon aria-hidden className="m-icon" />
    </EventStatusIcon>
  );
};
